import React, { useState, memo } from "react";
import InputMask from "react-input-mask";
import { MakePayment } from "../../redux/actions/payments";
import { connect } from "react-redux";
import valid from "card-validator";
import { Validation } from "../../helpers/validations";
import { errors, spanishErrors, cardImages, states } from "./constants";
import WrapperComponent from "../wrapperComponent";
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { calculateProcessingFee } from "./feeCalculation";

const sectionLabelStyle = {
  fontSize: '11px',
  fontWeight: 600,
  textTransform: 'uppercase',
  letterSpacing: '0.5px',
  color: '#6c757d',
  marginBottom: '8px',
  marginTop: '4px',
};

const sectionDividerStyle = {
  borderTop: '1px solid #d5dbe0',
  margin: '14px 0 10px',
};

const rowStyle = {
  display: 'flex',
  justifyContent: 'space-between',
  padding: '7px 0',
  fontSize: '13px',
  lineHeight: '1.5',
};

const labelStyle = { color: '#6c757d' };

const PaymentForm = memo(({ MakePayment, loading, user = {}, setIsCompleted, setIsFailed, setOpenPaymentAlert, closeModal, setTransactionResponse, resfreshData, setRefreshData }) => {
  const [amount, setAmount] = useState("");
  const [cardNumber, setCardNumber] = useState("");
  const [cardCode, setCardCode] = useState("");
  const [expirationDate, setExpiration] = useState("");
  const [showCardNumber, setShowCardNumber] = useState(false);
  const [cardImage, setCardImage] = useState(cardImages["default"]);
  const [error, setError] = useState({});
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [address, setAddress] = useState({
    firstname: "",
    lastname: "",
    addressLine1: "",
    addressLine2: "",
    city: "",
    state: "",
    postalCode: "",
  });
  const { t } = useTranslation();
  const feeInfo = calculateProcessingFee(amount);
  const lang = localStorage.getItem('i18nextLng') === "es";

  const onCompletion = (data) => {
    try {
      closeModal(true);
      if (data?.data?.response_code) {
        setIsFailed(data.data.response_code !== '100');
        setTransactionResponse(data.data.response_text);
        setRefreshData && setRefreshData(!resfreshData);
        setIsCompleted(true);
        setOpenPaymentAlert(true);
      } else {
        setIsFailed(true);
        setTransactionResponse(data?.message || data?.error);
        setOpenPaymentAlert(true);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleValidation = (type) => {
    if (type === "amount") {
      if (!amount || isNaN(parseFloat(amount)) || parseFloat(amount) <= 0) {
        setError(prev => ({ ...prev, amount: lang ? `${spanishErrors.amount}0.01` : `${errors.amount}0.01` }));
        return false;
      }
    } else if (type === "cardNumber") {
      if (!valid.number(cardNumber, { luhnValidateUnionPay: true }).isValid) {
        setError(prev => ({ ...prev, cardNumber: lang ? spanishErrors.cardNumber : errors.cardNumber }));
        return false;
      }
    } else if (type === "expirationDate") {
      let expireValidation = valid.expirationDate(expirationDate);
      if (!expireValidation.isPotentiallyValid || !expireValidation.isValid) {
        let msg = lang ? spanishErrors.expirationDate : errors.expirationDate;
        if (moment(expirationDate, 'MM/YYYY').diff(moment.now()) < 0) {
          msg = lang ? spanishErrors.cardExpired : errors.cardExpired;
        }
        setError(prev => ({ ...prev, expirationDate: msg }));
        return false;
      }
    } else if (type === "cardCode") {
      if (cardCode.length < 3 || cardCode.length > 4) {
        setError(prev => ({ ...prev, cardCode: lang ? spanishErrors.cardCode : errors.cardCode }));
        return false;
      }
    } else if (type === "firstname") {
      if (Validation.empty(address.firstname)) {
        setError(prev => ({ ...prev, firstname: lang ? spanishErrors.name : errors.name }));
        return false;
      }
    } else if (type === "lastname") {
      if (Validation.empty(address.lastname)) {
        setError(prev => ({ ...prev, lastname: lang ? spanishErrors.name : errors.name }));
        return false;
      }
    } else if (type === "addressLine1") {
      if (Validation.empty(address.addressLine1)) {
        setError(prev => ({ ...prev, addressLine1: lang ? spanishErrors.addressLine1 : errors.addressLine1 }));
        return false;
      }
    } else if (type === "city") {
      if (Validation.empty(address.city)) {
        setError(prev => ({ ...prev, city: lang ? spanishErrors.city : errors.city }));
        return false;
      }
    } else if (type === "state") {
      if (Validation.empty(address.state)) {
        setError(prev => ({ ...prev, state: lang ? spanishErrors.state : errors.state }));
        return false;
      }
    } else if (type === "postalCode") {
      if (!Validation.postalCode(address.postalCode)) {
        setError(prev => ({ ...prev, postalCode: lang ? spanishErrors.postalCode : errors.postalCode }));
        return false;
      }
    }
    return true;
  };

  const clearError = (field) => setError(prev => ({ ...prev, [field]: "" }));

  const handleReview = (e) => {
    e.preventDefault();
    const allFields = [
      "amount", "cardNumber", "expirationDate", "cardCode",
      "firstname", "lastname", "addressLine1", "city", "state", "postalCode"
    ];
    for (const field of allFields) {
      if (!handleValidation(field)) return;
    }
    setShowConfirmation(true);
  };

  const handleConfirmPayment = () => {
    const expDate = expirationDate.split('/');
    const formattedExp = expDate.length === 2 ? `${expDate[0]}${expDate[1].slice(-2)}` : expirationDate;
    const numberValidation = valid.number(cardNumber);

    MakePayment({
      ccnumber: cardNumber.toString(),
      ccexp: formattedExp,
      cvv: cardCode.toString(),
      amount: parseFloat(amount).toFixed(2),
      first_name: address.firstname,
      last_name: address.lastname,
      address1: address.addressLine1,
      address2: address.addressLine2,
      city: address.city,
      state: address.state,
      zip: address.postalCode,
      country: "US",
      leaseId: user.activeLease || "",
      peplId: user.zzID || "",
      email: user.Email_01,
      type: "Credit Card",
      type_more: numberValidation?.card?.type,
    }, onCompletion);
  };

  const handleCardNumberChange = (value) => {
    const numberValidation = valid.number(value);
    setCardNumber(value);
    setCardImage(
      numberValidation.isPotentiallyValid && numberValidation.card
        ? cardImages[numberValidation.card.type] || cardImages["default"]
        : cardImages["default"]
    );
  };

  const handleAmountChange = (value) => {
    const clean = value.replace(/[^0-9.]/g, "");
    const dotIdx = clean.indexOf(".");
    if (dotIdx !== -1) {
      setAmount(clean.substring(0, dotIdx) + "." + clean.substring(dotIdx + 1).replace(/\./g, "").slice(0, 2));
    } else {
      setAmount(clean);
    }
  };

  const handleAddressChange = (key, value) => {
    setAddress(prev => ({ ...prev, [key]: value }));
  };

  const maskValue = (val) => '*'.repeat(val.length);

  const rent = user?.leaseDetails?.Rent_Monthly_chargeTenant;
  const cardBrand = valid.number(cardNumber)?.card?.niceType || 'Card';
  const maskedCard = cardNumber.length >= 4 ? `**** ${cardNumber.slice(-4)}` : '';
  const addressLine = [
    address.addressLine1,
    address.addressLine2,
  ].filter(Boolean).join(', ');
  const cityStateZip = [address.city, address.state, address.postalCode].filter(Boolean).join(', ');

  if (showConfirmation) {
    return (
      <section className="paymentform-container">
        <div style={{ textAlign: 'center', marginBottom: '16px' }}>
          <i className="fa fa-shield-alt" style={{ fontSize: '28px', color: '#348fe2', marginBottom: '6px' }} />
          <h5 style={{ fontWeight: 600, margin: '4px 0 0' }}>{t('modal.payment.review-title', 'Review Your Payment')}</h5>
        </div>

        {/* Card & Billing */}
        <div style={sectionLabelStyle}>{t('modal.payment.card-details', 'Card Information')}</div>
        <div style={rowStyle}>
          <span style={labelStyle}>{t('modal.payment.card', 'Card')}</span>
          <span>{cardBrand} {maskedCard}</span>
        </div>
        <div style={rowStyle}>
          <span style={labelStyle}>{t('modal.payment.expires', 'Expires')}</span>
          <span>{expirationDate}</span>
        </div>

        <div style={{ ...sectionDividerStyle, marginTop: '6px' }} />
        <div style={sectionLabelStyle}>{t('modal.payment.billing-address', 'Billing Address')}</div>
        <div style={rowStyle}>
          <span style={labelStyle}>{t('modal.payment.name', 'Name')}</span>
          <span>{address.firstname} {address.lastname}</span>
        </div>
        {addressLine && (
          <div style={rowStyle}>
            <span style={labelStyle}>{t('modal.payment.address', 'Address')}</span>
            <span style={{ textAlign: 'right' }}>{addressLine}</span>
          </div>
        )}
        {cityStateZip && (
          <div style={rowStyle}>
            <span style={labelStyle}></span>
            <span style={{ textAlign: 'right' }}>{cityStateZip}</span>
          </div>
        )}

        {/* Payment Breakdown */}
        {feeInfo && (
          <>
            <div style={{ ...sectionDividerStyle, marginTop: '6px' }} />
            <div style={sectionLabelStyle}>{t('modal.payment.payment-summary', 'Payment Summary')}</div>
            <div style={rowStyle}>
              <span style={labelStyle}>{t('modal.payment.payment-amount', 'Rent Amount')}</span>
              <span>${feeInfo.baseAmount.toFixed(2)}</span>
            </div>
            <div style={rowStyle}>
              {/* <span style={labelStyle}>{t('modal.payment.processing-fee', 'Processing Fee')} ({feeInfo.feePercent}%)</span> */}
              <span style={labelStyle}>{t('modal.payment.processing-fee', 'Processing Fee')}</span>
              <span>${feeInfo.feeAmount.toFixed(2)}</span>
            </div>
            <div style={{ borderTop: '1px solid #d5dbe0', marginTop: '4px', paddingTop: '8px', display: 'flex', justifyContent: 'space-between', fontSize: '15px' }}>
              <strong>{t('modal.payment.total-charge', 'Total Charge')}</strong>
              <strong>${feeInfo.totalCharge.toFixed(2)}</strong>
            </div>
          </>
        )}

        {/* Actions */}
        <div style={{ display: 'flex', gap: '10px', marginTop: '18px' }}>
          <button
            className="btn btn-secondary"
            style={{ flex: '0 0 100px', padding: '10px', fontSize: '14px' }}
            onClick={() => setShowConfirmation(false)}
          >
            {t('modal.payment.back', 'Back')}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="btn btn-primary"
            style={{ flex: 1, padding: '10px', fontSize: '14px', fontWeight: 600 }}
            onClick={() => handleConfirmPayment()}
          >
            {!loading ? (
              feeInfo
                ? `Pay $${feeInfo.totalCharge.toFixed(2)}`
                : 'Pay'
            ) : (
              <i className="fas fa-spinner fa-pulse"></i>
            )}
          </button>
        </div>
      </section>
    );
  }

  return (
    <section className="paymentform-container">
      {/* --- Amount Section --- */}
      <div style={sectionLabelStyle}>{t('modal.payment.enter-amount', 'Rent Amount')}</div>
      {rent && (
        <p style={{ fontSize: '12px', color: '#6c757d', margin: '0 0 6px' }}>
          {t('modal.payment.start-text', 'Monthly rent')}: <strong>${parseFloat(rent).toFixed(2)}</strong>
        </p>
      )}
      <div className="form-group m-b-10">
        <div className="input-group">
          <div className="input-group-prepend">
            <span className="input-group-text"><i className="fa fa-dollar-sign" /></span>
          </div>
          <input
            type="text"
            className={`form-control form-control-lg ${error.amount ? "is-invalid" : ""}`}
            placeholder="0.00"
            value={amount}
            onChange={(e) => handleAmountChange(e.target.value)}
            onBlur={() => handleValidation("amount")}
            onFocus={() => clearError("amount")}
          />
        </div>
        {error.amount && <div className="invalid-feedback d-block">{error.amount}</div>}
      </div>

      {/* --- Card Details Section --- */}
      <div style={sectionDividerStyle} />
      <div style={sectionLabelStyle}>{t('modal.payment.card-details', 'Card Information')}</div>

      <div className="form-group m-b-10">
        <div className="input-group">
          <input
            type="text"
            className={`form-control form-control-lg ${error.cardNumber ? "is-invalid" : ""}`}
            placeholder={t('modal.payment.cardno', 'Card number')}
            value={showCardNumber ? cardNumber : maskValue(cardNumber)}
            onChange={(e) => handleCardNumberChange(e.target.value.replace(/[^0-9]/g, ""))}
            onBlur={() => { handleValidation("cardNumber"); setShowCardNumber(false); }}
            onFocus={() => { clearError("cardNumber"); setShowCardNumber(true); }}
          />
          <div className="input-group-append">
            <span className="input-group-text" style={{ background: '#fff' }}>
              <i className={cardImage} style={{ fontSize: '1.4em' }} />
            </span>
          </div>
        </div>
        {error.cardNumber && <div className="invalid-feedback d-block">{error.cardNumber}</div>}
      </div>

      <div className="row">
        <div className="col-7 pr-1">
          <div className="form-group m-b-10">
            <InputMask
              mask="99/9999"
              className={`form-control form-control-lg ${error.expirationDate ? "is-invalid" : ""}`}
              value={expirationDate}
              placeholder="MM/YYYY"
              onChange={(e) => setExpiration(e.target.value)}
              onBlur={() => handleValidation("expirationDate")}
              onFocus={() => clearError("expirationDate")}
            />
            {error.expirationDate && <div className="invalid-feedback d-block">{error.expirationDate}</div>}
          </div>
        </div>
        <div className="col-5 pl-1">
          <div className="form-group m-b-10">
            <input
              type="password"
              className={`form-control form-control-lg ${error.cardCode ? "is-invalid" : ""}`}
              placeholder={t('modal.payment.cardcode', 'CVV')}
              value={cardCode}
              onChange={(e) => setCardCode(e.target.value.replace(/[^0-9]/g, "").slice(0, 4))}
              onBlur={() => handleValidation("cardCode")}
              onFocus={() => clearError("cardCode")}
            />
            {error.cardCode && <div className="invalid-feedback d-block">{error.cardCode}</div>}
          </div>
        </div>
      </div>

      {/* --- Billing Address Section --- */}
      <div style={sectionDividerStyle} />
      <div style={sectionLabelStyle}>{t('modal.payment.billing-address', 'Billing Address')}</div>

      <div className="row">
        <div className="col-6 pr-1">
          <div className="form-group m-b-10">
            <input
              type="text"
              className={`form-control form-control-lg ${error.firstname ? "is-invalid" : ""}`}
              placeholder={t('modal.payment.firstname', 'First name')}
              value={address.firstname}
              onChange={(e) => handleAddressChange("firstname", e.target.value)}
              onBlur={() => handleValidation("firstname")}
              onFocus={() => clearError("firstname")}
            />
            {error.firstname && <div className="invalid-feedback d-block">{error.firstname}</div>}
          </div>
        </div>
        <div className="col-6 pl-1">
          <div className="form-group m-b-10">
            <input
              type="text"
              className={`form-control form-control-lg ${error.lastname ? "is-invalid" : ""}`}
              placeholder={t('modal.payment.lastname', 'Last name')}
              value={address.lastname}
              onChange={(e) => handleAddressChange("lastname", e.target.value)}
              onBlur={() => handleValidation("lastname")}
              onFocus={() => clearError("lastname")}
            />
            {error.lastname && <div className="invalid-feedback d-block">{error.lastname}</div>}
          </div>
        </div>
      </div>

      <div className="form-group m-b-10">
        <input
          type="text"
          className={`form-control form-control-lg ${error.addressLine1 ? "is-invalid" : ""}`}
          placeholder={t('modal.payment.addL1', 'Address line 1')}
          value={address.addressLine1}
          onChange={(e) => handleAddressChange("addressLine1", e.target.value)}
          onBlur={() => handleValidation("addressLine1")}
          onFocus={() => clearError("addressLine1")}
        />
        {error.addressLine1 && <div className="invalid-feedback d-block">{error.addressLine1}</div>}
      </div>

      <div className="form-group m-b-10">
        <input
          type="text"
          className="form-control form-control-lg"
          placeholder={t('modal.payment.addL2', 'Address line 2 (optional)')}
          value={address.addressLine2}
          onChange={(e) => handleAddressChange("addressLine2", e.target.value)}
        />
      </div>

      <div className="row">
        <div className="col-5 pr-1">
          <div className="form-group m-b-10">
            <input
              type="text"
              className={`form-control form-control-lg ${error.city ? "is-invalid" : ""}`}
              placeholder={t('modal.payment.city', 'City')}
              value={address.city}
              onChange={(e) => handleAddressChange("city", e.target.value)}
              onBlur={() => handleValidation("city")}
              onFocus={() => clearError("city")}
            />
            {error.city && <div className="invalid-feedback d-block">{error.city}</div>}
          </div>
        </div>
        <div className="col-4 px-1">
          <div className="form-group m-b-10">
            <select
              className={`form-control form-control-lg ${error.state ? "border border-danger" : ""}`}
              value={address.state}
              onChange={(e) => handleAddressChange("state", e.target.value)}
              onBlur={() => handleValidation("state")}
              onFocus={() => clearError("state")}
            >
              <option value="">{t('modal.payment.select-state', 'State')}</option>
              {states.map((item, i) => (
                <option key={i} value={item.State}>{item.Code}</option>
              ))}
            </select>
            {error.state && <div className="invalid-feedback d-block">{error.state}</div>}
          </div>
        </div>
        <div className="col-3 pl-1">
          <div className="form-group m-b-10">
            <input
              type="text"
              className={`form-control form-control-lg ${error.postalCode ? "is-invalid" : ""}`}
              placeholder={t('modal.payment.postal', 'ZIP')}
              value={address.postalCode}
              onChange={(e) => handleAddressChange("postalCode", e.target.value.replace(/[^0-9\-]/g, ""))}
              onBlur={() => handleValidation("postalCode")}
              onFocus={() => clearError("postalCode")}
            />
            {error.postalCode && <div className="invalid-feedback d-block">{error.postalCode}</div>}
          </div>
        </div>
      </div>

      {/* --- Fee Summary --- */}
      {feeInfo && (
        <>
          <div style={sectionDividerStyle} />
          <div style={{ fontSize: '13px', marginBottom: '12px' }}>
            <div className="d-flex justify-content-between mb-1">
              <span style={{ color: '#6c757d' }}>{t('modal.payment.payment-amount', 'Rent Amount')}</span>
              <span>${feeInfo.baseAmount.toFixed(2)}</span>
            </div>
            <div className="d-flex justify-content-between mb-1">
              {/* <span style={{ color: '#6c757d' }}>{t('modal.payment.processing-fee', 'Processing Fee')} ({feeInfo.feePercent}%)</span> */}
              <span style={{ color: '#6c757d' }}>{t('modal.payment.processing-fee', 'Processing Fee')}</span>
              <span>${feeInfo.feeAmount.toFixed(2)}</span>
            </div>
            <div style={{ borderTop: '1px solid #d5dbe0', paddingTop: '6px', marginTop: '4px' }} className="d-flex justify-content-between">
              <strong>{t('modal.payment.total-charge', 'Total Charge')}</strong>
              <strong>${feeInfo.totalCharge.toFixed(2)}</strong>
            </div>
          </div>
        </>
      )}

      {/* --- Submit --- */}
      <button
        type="submit"
        disabled={loading}
        className="btn btn-warning btn-block"
        style={{ padding: '10px', fontSize: '15px', fontWeight: 600 }}
        onClick={handleReview}
      >
        {feeInfo
          ? `${t('modal.payment.review-pay', 'Review & Pay')} $${feeInfo.totalCharge.toFixed(2)}`
          : t('modal.payment.review-pay', 'Review & Pay')
        }
      </button>
    </section>
  );
});

const mapStateToProps = ({ PAYMENT_REDUCER, USER_REDUCER }) => {
  return { loading: PAYMENT_REDUCER.loading, user: USER_REDUCER.userData };
};

const PaymentWrapper = (props) => (
  <WrapperComponent>
    <PaymentForm {...props} />
  </WrapperComponent>
);

export default connect(mapStateToProps, { MakePayment })(PaymentWrapper);
